import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const EditSingleProductIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="To edit a single product, follow these steps:"
          description={{
            text: "Introduction to editing a single product",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to edit a product",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the product you want to edit."
              description={{
                text: "Second step to edit a product",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="On the product page, you can:"
          description={{
            text: "Third step to edit a product",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Edit product details."
              description={{
                text: "Edit product details",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Mark the product and its individual options as out of stock by updating inventory."
              description={{
                text: "Mark product as out of stock",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Preview changes."
              description={{
                text: "Preview changes",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Publish or unpublish the product from your store."
              description={{
                text: "Publish or unpublish product",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Save a draft of the product."
              description={{
                text: "Save a draft",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Delete the product."
              description={{
                text: "Delete product",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="After making changes, select the <BoldFormatting>Update</BoldFormatting> button in the top right-hand corner of the page to save. If any updates can’t be completed, you’ll see an error message explaining how to resolve the issue."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Steps to save changes",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you don’t see the changes reflected on your product, it may be due to delays with updating. If the issue persists for more than 24 hours, or if you need help understanding any error messages, contact us for more help."
          description={{
            text: "Contact us for help if changes aren't reflected",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Keep in mind that edits made to a product will only impact new orders and won’t affect current unfulfilled orders."
          description={{
            text: "Note about product edits",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
